import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { FormField } from '@/components/forms/FormField'
import { EmptyState } from '@/components/ui/EmptyState'
import {
  productService,
  type ItemCodeHistory,
  type Product,
} from '@/services/products'

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString('vi-VN')
  } catch {
    return iso
  }
}

export function ProductChangeCodePage() {
  const { productId = '' } = useParams()
  const navigate = useNavigate()
  const [product, setProduct] = useState<Product | null>(null)
  const [history, setHistory] = useState<ItemCodeHistory[]>([])
  const [newCode, setNewCode] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function load(id: string) {
    setLoading(true)
    const p = await productService.getById(id)
    setProduct(p)
    if (p) {
      const rows = await productService.listCodeHistory(p.id)
      setHistory(rows)
    } else {
      setHistory([])
    }
    setLoading(false)
  }

  useEffect(() => {
    void load(productId)
  }, [productId])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!product) return
    setError(null)
    setSaving(true)
    try {
      const updated = await productService.changeItemCode(product.id, {
        newCode,
        reason,
      })
      navigate(`/products/${updated.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể đổi mã hàng')
      setSaving(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Đang tải...</p>
  }

  if (!product) {
    return (
      <EmptyState
        title="Không tìm thấy mã hàng"
        description="product_id không tồn tại."
        action={
          <Button variant="secondary" onClick={() => navigate('/products')}>
            Quay lại danh sách
          </Button>
        }
      />
    )
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mã hàng', to: '/products' },
          { label: product.code, to: `/products/${product.id}` },
          { label: 'Đổi mã' },
        ]}
      />
      <PageHeader
        title={`Đổi mã hàng — ${product.code}`}
        subtitle="product_id giữ nguyên; item_code đổi có lịch sử. URL không đổi."
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white p-5"
        >
          {error ? (
            <div className="rounded-[var(--radius-sm)] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <FormField label="Mã hiện tại">
            <input
              disabled
              value={product.code}
              className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-slate-50 px-3 py-2 text-sm text-slate-500"
            />
          </FormField>

          <FormField label="Mã mới" required>
            <input
              required
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toUpperCase())}
              className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
              placeholder="VD: MP176"
            />
          </FormField>

          <FormField label="Lý do đổi mã" required>
            <textarea
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
              placeholder="Khách đổi spec / chuẩn hóa mã nội bộ..."
            />
          </FormField>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/products/${product.id}`)}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Đang đổi...' : 'Xác nhận đổi mã'}
            </Button>
          </div>
        </form>

        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white p-5">
          <h2 className="mb-3 text-sm font-semibold text-[var(--color-text)]">
            Lịch sử đổi mã
          </h2>
          {history.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              Chưa có lần đổi mã nào cho sản phẩm này.
            </p>
          ) : (
            <ul className="space-y-3">
              {history.map((row) => (
                <li
                  key={row.id}
                  className="rounded-[var(--radius-sm)] border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                >
                  <div className="font-medium text-[var(--color-text)]">
                    {row.oldCode} → {row.newCode}
                  </div>
                  <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                    {formatWhen(row.changedAt)} · {row.changedBy}
                  </div>
                  <div className="mt-1 text-slate-700">{row.reason}</div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-[var(--color-text-muted)]">
            Quay lại{' '}
            <Link
              to={`/products/${product.id}`}
              className="text-[var(--color-link)] hover:underline"
            >
              chi tiết mã hàng
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  )
}
